import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { PriceProvider } from './context/PriceContext';
import Header from './components/Header';
import Hero from './components/Hero';
import Features from './components/Features';
import Footer from './components/Footer';
import HomeOrDashboard from './pages/HomeOrDashboard';
import SignupPage from './pages/SignupPage';
import LoginPage from './pages/LoginPage';
import SurveyRedirect from './pages/SurveyRedirect';
import AuthCallback from './pages/AuthCallback';
import OnboardingLanding from './pages/OnboardingLanding';
import OnboardingSetup from './pages/OnboardingSetup';
import QuickSearch from './pages/QuickSearch';
import QuickWizard from './components/QuickWizard';
import ProfilePage from './pages/ProfilePage';
import MyKitchen from './pages/MyKitchen';
import MyFavorites from './pages/MyFavorites';
import AddMyKitchen from './pages/AddMyKitchen';
import AppLayout from './layouts/AppLayout';
import DashboardLayout from './layouts/DashboardLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminRecipeIngestion from './pages/admin/AdminRecipeIngestion';
import AdminLogin from './pages/admin/AdminLogin';
import AdminProtected from './pages/admin/AdminProtected';
import AdminUsers from './pages/admin/AdminUsers';
import AdminIngredients from './pages/admin/AdminIngredients';
import AdminSurveys from './pages/admin/AdminSurveys';
import AdminAdvertisements from './pages/admin/AdminAdvertisements';
import AdminAllergens from './pages/admin/AdminAllergens';
import AdminSubscriptions from './pages/admin/AdminSubscriptions';
import BehaviorTemplatesPage from './pages/admin/BehaviorTemplatesPage';
import BehaviorInsightsPage from './pages/admin/BehaviorInsightsPage';
import BehaviorMetricsPage from './pages/admin/BehaviorMetricsPage';
import PendingSurveys from './pages/admin/PendingSurveys';
import AdminNewUserSurvey from './pages/admin/AdminNewUserSurvey';
import AdminRegistrationSurvey from './pages/admin/AdminRegistrationSurvey';
import RegistrationSurvey from './pages/RegistrationSurvey';
import AllergenScanner from './pages/AllergenScanner';
import NotificationsPage from './pages/Notifications';
import AdminNotifications from './pages/admin/AdminNotifications';
import AdminDataPool from './pages/admin/AdminDataPool';
import AdminHelpPage from './pages/admin/AdminHelpPage';
import AdminPriceIntelligence from './pages/AdminPriceIntelligence';
import BabyNameModal from './components/BabyNameModal';
import ShoppingList from './pages/ShoppingList';
import MyShoppingLists from './pages/MyShoppingLists';
import SmartShoppingList from './pages/SmartShoppingList';
import SharedMarketList from './pages/SharedMarketList';
import PartnerAccept from './pages/PartnerAccept';
import PartnerRegistration from './pages/PartnerRegistration';
import PartnerDashboard from './pages/PartnerDashboard';
import KahvaltiTakibi from './pages/KahvaltiTakibi';
import MarketList from './pages/MarketList';
import KitchenPlanner from './pages/KitchenPlanner';
import AlakartMenu from './pages/AlakartMenu';
import AdminConfig from './pages/AdminConfig';
import SmartSearchPage from './pages/SmartSearchPage';
import AllergenKitchen from './pages/AllergenKitchen';
import DietKitchen from './pages/DietKitchen.jsx';
import DietitianInvite from './pages/DietitianInvite.jsx';
import AthleteKitchen from './pages/AthleteKitchen.jsx';
import PTInvite from './pages/PTInvite.jsx';
import DietitianPanel from './pages/DietitianPanel.jsx';
import PTPanel from './pages/PTPanel.jsx';
import ProfessionalLoginChoice from './pages/ProfessionalLoginChoice.jsx';
import ProfessionalInviteAccept from './pages/ProfessionalInviteAccept.jsx';
import TodayMenuPage from './pages/TodayMenuPage';
import AlakartFamilySurvey from './pages/AlakartFamilySurvey.jsx';
import FamilyRatingPage from './pages/FamilyRatingPage.jsx';
import AllRecipesPage from './pages/AllRecipesPage.jsx';
import LandingLab from './pages/LandingLab.jsx';
import SpecialDaysKitchen from './pages/SpecialDaysKitchen.jsx';
import HealthPlusHub from './pages/HealthPlusHub.jsx';
import FaqPage from './pages/FaqPage.jsx';
import HowItWorksPage from './pages/HowItWorksPage.jsx';
import OnboardingJourneyPreview from './pages/OnboardingJourneyPreview.jsx';
import AdminFaqManager from './pages/admin/AdminFaqManager.jsx';
import AdminUsageGuide from './pages/admin/AdminUsageGuide.jsx';
import BlogLandingPage from './pages/BlogLandingPage.jsx';
import BlogDetailPage from './pages/BlogDetailPage.jsx';
import BlogCategoryPage from './pages/BlogCategoryPage.jsx';
import AboutPage from './pages/AboutPage.jsx';
import AdminBlogPage from './pages/admin/AdminBlogPage.jsx';
import RealtimeBridge from './components/RealtimeBridge.jsx';
const AdminPolicies = React.lazy(() => import('./pages/admin/AdminPolicies'));
const AdminSeededPrices = React.lazy(() => import('./pages/admin/AdminSeededPrices'));
const AdminRecipeManagePage = React.lazy(() => import('./pages/admin/AdminRecipeManagePage'));
const AdminCategoryImages = React.lazy(() => import('./pages/admin/AdminCategoryImages'));
const AdminDamping = React.lazy(() => import('./pages/admin/AdminDamping'));
const AdminUnresolved = React.lazy(() => import('./pages/admin/AdminUnresolved'));
const AdminOnboarding = React.lazy(() => import('./pages/admin/AdminOnboarding'));
const AdminProfileStarterMenu = React.lazy(() => import('./pages/admin/AdminProfileStarterMenu'));
const AdminPricingMvp = React.lazy(() => import('./pages/admin/AdminPricingMvp'));
const AdminMarketReference = React.lazy(() => import('./pages/admin/AdminMarketReference'));
const AdminIngredientCandidates = React.lazy(() => import('./pages/admin/AdminIngredientCandidates'));
const AdminIngredientGovernance = React.lazy(() => import('./pages/admin/AdminIngredientGovernance'));
const AdminRecipeFeedbackCenter = React.lazy(() => import('./pages/admin/AdminRecipeFeedbackCenter'));
import useBabyNameModal from './hooks/useBabyNameModal';
import { API_BASE_URL } from './config';

const SITE_IDENTITY_JSON_LD = {
    "@context": "https://schema.org",
    "@graph": [
        {
            "@type": "WebSite",
            "@id": "https://banabirtarifver.com/#website",
            "url": "https://banabirtarifver.com/",
            "name": "Bana Bir Tarif Ver",
            "inLanguage": "tr-TR",
            "description": "Ev mutfağını stok, bütçe, porsiyon ve günlük yemek kararlarıyla birlikte yöneten mutfak yönetim sistemi."
        },
        {
            "@type": "Organization",
            "@id": "https://banabirtarifver.com/#organization",
            "name": "Bana Bir Tarif Ver",
            "url": "https://banabirtarifver.com/",
            "logo": "https://banabirtarifver.com/logo-bbtv.png"
        },
        {
            "@type": "SoftwareApplication",
            "@id": "https://banabirtarifver.com/#software",
            "name": "Bana Bir Tarif Ver",
            "applicationCategory": "LifestyleApplication",
            "operatingSystem": "Web, Android, iOS",
            "url": "https://banabirtarifver.com/",
            "isAccessibleForFree": true,
            "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "TRY"
            },
            "description": "Ev mutfağını yönetmek için geliştirilen; stok takibi, yemek planlama, alışveriş yönetimi ve günlük mutfak karar desteği sağlayan web tabanlı mutfak yönetim sistemi.",
            "inLanguage": "tr-TR",
            "publisher": {
                "@id": "https://banabirtarifver.com/#organization"
            }
        }
    ]
};

function App() {
    // 1. Dark Mode State'i ve LocalStorage KontrolÃ¼
    const [isDarkMode, setIsDarkMode] = useState(
        () => {
            // LocalStorage'da kaydedilen 'darkMode' deÄŸerini kontrol eder
            const savedMode = localStorage.getItem('darkMode');
            // 'true' ise Dark Mode aktif baÅŸlar, deÄŸilse (veya yoksa) inaktif.
            return savedMode === 'true';
        }
    );

    const location = useLocation();
    const isSharedGuestRoute = location.pathname.startsWith('/shared/');
    const globalBabyNameModalBlockedRoutes = new Set(['/my-kitchen', '/quick-search']);
    const shouldRenderGlobalBabyNameModal = !globalBabyNameModalBlockedRoutes.has(location.pathname);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, [location.pathname, location.search]);

    useEffect(() => {
        const scriptId = 'site-identity-jsonld-script';
        const existing = document.getElementById(scriptId);
        const isFaqRoute = location.pathname === '/sss' || location.pathname === '/faq';

        if (isFaqRoute) {
            if (existing?.parentNode) existing.parentNode.removeChild(existing);
            return;
        }

        const scriptEl = existing || document.createElement('script');
        scriptEl.id = scriptId;
        scriptEl.type = 'application/ld+json';
        scriptEl.textContent = JSON.stringify(SITE_IDENTITY_JSON_LD);
        if (!existing) document.head.appendChild(scriptEl);
    }, [location.pathname]);

    // 2. Dark Mode SÄ±nÄ±fÄ±nÄ± (dark/light) <html> elementine Uygulama ve DB ile Senkronizasyon
    useEffect(() => {
        const root = document.documentElement;
        if (isDarkMode) {
            root.classList.add('dark');
            root.classList.remove('light');
            localStorage.setItem('darkMode', 'true');
        } else {
            root.classList.remove('dark');
            root.classList.add('light');
            localStorage.setItem('darkMode', 'false');
        }
    }, [isDarkMode]);

    // Keep mobile status bar color in sync with app theme (Android/PWA).
    useEffect(() => {
        const themeMeta = document.querySelector('meta[name="theme-color"]');
        if (themeMeta) {
            themeMeta.setAttribute('content', isDarkMode ? '#0b1220' : '#f8fafc');
        }
    }, [isDarkMode]);

    // DB'den Dark Mode AyarÄ±nÄ± Ã‡ek (GiriÅŸ sonrasÄ± veya sayfa geÃ§iÅŸlerinde senkronizasyon iÃ§in)
    useEffect(() => {
        const fetchProfileDarkMode = async () => {
            const token = localStorage.getItem('authToken');
            if (!token) return;

            try {
                const res = await fetch(`${API_BASE_URL}/api/profile`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const profile = await res.json();
                    if (profile && profile.darkMode !== undefined) {
                        if (profile.darkMode !== isDarkMode) {
                            setIsDarkMode(profile.darkMode);
                            localStorage.setItem('darkMode', profile.darkMode ? 'true' : 'false');
                        }
                    }
                }
            } catch (e) {
                console.warn('ğŸŒ— [App] Dark mode sync failed error:', e);
            }
        };
        fetchProfileDarkMode();
    }, [location.pathname]);

    // 3. WakeLock - CihazÄ±n uyku moduna geÃ§mesini engelle
    useEffect(() => {
        if (!('wakeLock' in navigator)) {
            console.log('âš ï¸ WakeLock API desteklenmiyor bu tarayÄ±cÄ±da');
            return undefined;
        }

        let wakeLock = null;

        const requestWakeLock = async () => {
            if (document.visibilityState !== 'visible') return;
            try {
                wakeLock = await navigator.wakeLock.request('screen');
                // Removed console.log for cleaner production/dev look
            } catch (err) {
                console.error(`âŒ WakeLock Error: ${err.name}`);
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                requestWakeLock();
            }
        };

        requestWakeLock();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (wakeLock) {
                wakeLock.release();
            }
        };
    }, []);

    // 3. Dark Mode'u DeÄŸiÅŸtiren Fonksiyon
    const toggleDarkMode = async () => {
        const newMode = !isDarkMode;
        setIsDarkMode(newMode);

        // VeritabanÄ±na Kaydet (Yeni Ã¶zel endpoint ile daha gÃ¼venli)
        try {
            const token = localStorage.getItem('authToken');
            if (token) {
                await fetch(`${API_BASE_URL}/api/profile/dark-mode`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ darkMode: newMode })
                });
            }
        } catch (e) {
            console.warn('Could not save dark mode to DB', e);
        }
    };

    // 4. Baby Name Modal Hook
    const {
        shouldShowModal,
        userProfile,
        loading,
        saveBabyName,
        skipForNow,
        hideModal
    } = useBabyNameModal();

    // 4. UygulamanÄ±n Ana YapÄ±sÄ±: YÃ¶nlendirme (Routing)
    const appRoutes = (
        <div className="relative w-full flex-col overflow-x-hidden">
            <Routes>

                    {/* 1. ANA SAYFA ROTASI (Header'a dark mode prop'larÄ± iletiliyor) */}
                    <Route path="/" element={<HomeOrDashboard isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />} />
                    <Route path="/landing-lab" element={<LandingLab isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />} />
                    <Route path="/sss" element={<FaqPage isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />} />
                    <Route path="/faq" element={<Navigate to="/sss" replace />} />
                    <Route path="/nasil-calisir" element={<HowItWorksPage isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />} />
                    <Route path="/baslangic-onizleme" element={<OnboardingJourneyPreview isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />} />
                    <Route path="/hakkimizda" element={<AboutPage isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />} />

                    {/* 2. KAYIT OL ROTASI (Dark mode prop'larÄ± iletiliyor) */}
                    <Route
                        path="/kayit-ol"
                        element={<SignupPage isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
                    />

                    {/* 3. GÄ°RÄ°Å YAP ROTASI (Dark mode prop'larÄ± iletiliyor) */}
                    <Route
                        path="/giris-yap"
                        element={<LoginPage isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
                    />

                    {/* 4. ANKET SAYFASI ROTASI (Dark mode prop'larÄ± iletiliyor) */}
                    <Route
                        path="/anket"
                        element={<SurveyRedirect />}
                    />

                    {/* HÄ±zlÄ± Kurulum SihirbazÄ± */}
                    <Route
                        path="/wizard"
                        element={<QuickWizard onClose={() => window.location.href = '/dashboard'} onComplete={() => window.location.href = '/my-kitchen'} />}
                    />

                    {/* Registration survey (user-facing standard onboarding survey) */}
                    <Route
                        path="/registration-survey"
                        element={<RegistrationSurvey isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
                    />

                    {/* Onboarding Yeni Full Page Setup Screen */}
                    <Route
                        path="/onboarding-setup"
                        element={<OnboardingSetup />}
                    />

                    {/* OAuth callback landing page - frontend will decide where to send user */}
                    <Route
                        path="/auth-callback"
                        element={<AuthCallback />}
                    />

                    {/* Public Shared Shopping List - No Auth Required */}
                    <Route
                        path="/shared/:token"
                        element={<SharedMarketList />}
                    />

                    <Route
                        path="/partner/accept/:token"
                        element={<PartnerAccept />}
                    />

                    {/* Dietitian Invite - No Auth Required */}
                    <Route
                        path="/dietitian-invite/:token"
                        element={<DietitianInvite />}
                    />

                    {/* PT Invite - No Auth Required */}
                    <Route
                        path="/pt-invite/:token"
                        element={<PTInvite />}
                    />
                    <Route
                        path="/professional-login-choice"
                        element={<ProfessionalLoginChoice />}
                    />
                    <Route
                        path="/professional/invite/:token"
                        element={<ProfessionalInviteAccept />}
                    />
                    <Route
                        path="/diyetisyen-panel"
                        element={<DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}><DietitianPanel /></DashboardLayout>}
                    />
                    <Route
                        path="/pt-panel"
                        element={<DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}><PTPanel /></DashboardLayout>}
                    />

                    {/* Partner Registration Onboarding - No Auth Required */}
                    <Route
                        path="/partner/register/:token"
                        element={<PartnerRegistration isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
                    />

                    {/* Micro-Dashboard for Partner (YardÄ±mcÄ± Åef) */}
                    <Route
                        path="/partner-dashboard"
                        element={<DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}><PartnerDashboard /></DashboardLayout>}
                    />

                    {/* 5. Dashboard / Onboarding Landing */}
                    <Route
                        path="/dashboard"
                        element={<OnboardingLanding isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
                    />
                    <Route
                        path="/profile"
                        element={<DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}><ProfilePage /></DashboardLayout>}
                    />
                    {/* Quick Search page */}
                    <Route
                        path="/quick-search"
                        element={<QuickSearch isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
                    />
                    {/* All Recipes page */}
                    <Route
                        path="/all-recipes"
                        element={<DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}><AllRecipesPage isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} /></DashboardLayout>}
                    />
                    <Route
                        path="/special-days-kitchen"
                        element={<SpecialDaysKitchen isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
                    />
                    <Route
                        path="/health-plus"
                        element={<HealthPlusHub isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
                    />
                    {/* MyKitchen page */}
                    <Route
                        path="/my-kitchen"
                        element={<DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}><MyKitchen isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} /></DashboardLayout>}
                    />
                    <Route
                        path="/my-favorites"
                        element={<DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}><MyFavorites /></DashboardLayout>}
                    />
                    <Route
                        path="/add-to-kitchen"
                        element={<DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}><AddMyKitchen /></DashboardLayout>}
                    />
                    <Route
                        path="/addmykitchen"
                        element={<DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}><AddMyKitchen /></DashboardLayout>}
                    />
                    <Route
                        path="/notifications"
                        element={<DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}><NotificationsPage isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} /></DashboardLayout>}
                    />
                    <Route
                        path="/in-app/sss"
                        element={<DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}><FaqPage isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} embedded /></DashboardLayout>}
                    />
                    <Route
                        path="/allergen-scan"
                        element={<DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}><AllergenScanner /></DashboardLayout>}
                    />
                    <Route
                        path="/shopping-list"
                        element={<DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}><ShoppingList /></DashboardLayout>}
                    />
                    <Route
                        path="/my-shopping-lists"
                        element={<DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}><MyShoppingLists /></DashboardLayout>}
                    />
                    <Route
                        path="/market-listesi"
                        element={<DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}><MarketList /></DashboardLayout>}
                    />
                    <Route
                        path="/kahvalti-takibi"
                        element={<DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}><KahvaltiTakibi /></DashboardLayout>}
                    />
                    <Route
                        path="/kitchen-planner"
                        element={<KitchenPlanner isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
                    />
                    <Route
                        path="/alakart-menu"
                        element={<AlakartMenu isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
                    />
                    <Route
                        path="/alakart/survey/:token"
                        element={<AlakartFamilySurvey />}
                    />
                    <Route
                        path="/alakart/rating/:token"
                        element={<FamilyRatingPage />}
                    />
                    <Route
                        path="/allergen-kitchen"
                        element={<AllergenKitchen isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
                    />
                    <Route
                        path="/diet-kitchen"
                        element={<DietKitchen isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
                    />
                    <Route
                        path="/athlete-kitchen"
                        element={<AthleteKitchen isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
                    />
                    <Route
                        path="/smart-fridge"
                        element={<SmartSearchPage isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
                    />
                    <Route
                        path="/today-menu"
                        element={<TodayMenuPage isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
                    />
                    <Route
                        path="/blog"
                        element={<BlogLandingPage isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
                    />
                    <Route
                        path="/blog/:categorySlug/:postSlug"
                        element={<BlogDetailPage isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
                    />
                    <Route
                        path="/blog/:categorySlug"
                        element={<BlogCategoryPage isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
                    />

                    {/* Admin route */}
                    <Route path="/admin/login" element={<AdminLogin />} />
                    {/* Admin protected area with nested routes (index -> dashboard, /users, /surveys, etc.) */}
                    <Route path="/admin/*" element={<AdminProtected />}>
                        <Route index element={<AdminDashboard isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />} />
                        <Route path="users" element={<React.Suspense fallback={<div className="p-8">YÃ¼kleniyor...</div>}><AdminUsers /></React.Suspense>} />
                        <Route path="subscriptions" element={<React.Suspense fallback={<div className="p-8">YÃ¼kleniyor...</div>}><AdminSubscriptions /></React.Suspense>} />
                        <Route path="ingredients" element={<React.Suspense fallback={<div className="p-8">YÃ¼kleniyor...</div>}><AdminIngredients /></React.Suspense>} />
                        <Route path="advertisements" element={<React.Suspense fallback={<div className="p-8">YÃ¼kleniyor...</div>}><AdminAdvertisements /></React.Suspense>} />
                        <Route path="notifications" element={<React.Suspense fallback={<div className="p-8">YÃ¼kleniyor...</div>}><AdminNotifications /></React.Suspense>} />
                        <Route path="pricing" element={<AdminPriceIntelligence />} />
                        <Route path="pricing/mvp" element={<React.Suspense fallback={<div className="p-8">YÃ¼kleniyor...</div>}><AdminPricingMvp /></React.Suspense>} />
                        <Route path="pricing/market-reference" element={<React.Suspense fallback={<div className="p-8">YÃ¼kleniyor...</div>}><AdminMarketReference /></React.Suspense>} />
                        <Route path="pricing/candidates" element={<React.Suspense fallback={<div className="p-8">YÃ¼kleniyor...</div>}><AdminIngredientCandidates /></React.Suspense>} />
                        <Route path="pricing/governance" element={<React.Suspense fallback={<div className="p-8">YÃ¼kleniyor...</div>}><AdminIngredientGovernance /></React.Suspense>} />
                        <Route path="recipe-feedback" element={<React.Suspense fallback={<div className="p-8">YÃ¼kleniyor...</div>}><AdminRecipeFeedbackCenter /></React.Suspense>} />
                        <Route path="product-analytics" element={<AdminDashboard initialTab="analytics" isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />} />
                        <Route path="segment-analysis" element={<AdminDashboard initialTab="segment-analysis" isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />} />
                        <Route path="behavior-templates" element={<BehaviorTemplatesPage />} />
                        <Route path="behavior-insights" element={<BehaviorInsightsPage />} />
                        <Route path="behavior-metrics" element={<BehaviorMetricsPage />} />
                        <Route path="pricing/recipe-feedback" element={<Navigate to="/admin/recipe-feedback" replace />} />
                        <Route path="pricing/seeded" element={<React.Suspense fallback={<div className="p-8">YÃ¼kleniyor...</div>}><AdminSeededPrices /></React.Suspense>} />
                        <Route path="pricing/damping" element={<React.Suspense fallback={<div className="p-8">YÃ¼kleniyor...</div>}><AdminDamping /></React.Suspense>} />
                        <Route path="veri-havuzu" element={<AdminDataPool />} />
                        <Route path="yardim" element={<AdminHelpPage />} />
                        <Route path="surveys" element={<AdminSurveys />} />
                        <Route path="surveys/new-user" element={<React.Suspense fallback={<div className="p-8">YÃ¼kleniyor...</div>}><AdminNewUserSurvey /></React.Suspense>} />
                        <Route path="surveys/registration" element={<React.Suspense fallback={<div className="p-8">YÃ¼kleniyor...</div>}><AdminRegistrationSurvey /></React.Suspense>} />
                        <Route path="surveys/pending" element={<PendingSurveys />} />
                        <Route path="policies" element={<React.Suspense fallback={<div className="p-8">YÃ¼kleniyor...</div>}><AdminPolicies /></React.Suspense>} />
                        <Route path="alerjanlar" element={<React.Suspense fallback={<div className="p-8">YÃ¼kleniyor...</div>}><AdminAllergens /></React.Suspense>} />
                        <Route path="config" element={<AdminConfig />} />
                        <Route path="category-images" element={<React.Suspense fallback={<div className="p-8">YÃ¼kleniyor...</div>}><AdminCategoryImages /></React.Suspense>} />
                        <Route path="recipes-manage" element={<AdminRecipeIngestion initialTab="MANAGE" mode="manage-only" />} />
                        <Route path="chef-entry" element={<AdminRecipeIngestion initialTab="MANAGE" mode="manage-only" />} />
                        <Route path="pricing/unresolved" element={<React.Suspense fallback={<div className="p-8">YÃ¼kleniyor...</div>}><AdminUnresolved /></React.Suspense>} />
                        <Route path="onboarding" element={<React.Suspense fallback={<div className="p-8">YÃ¼kleniyor...</div>}><AdminOnboarding isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} /></React.Suspense>} />
                        <Route path="onboarding/starter-menu" element={<React.Suspense fallback={<div className="p-8">YÃ¼kleniyor...</div>}><AdminProfileStarterMenu isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} /></React.Suspense>} />
                        <Route path="faqs" element={<AdminFaqManager />} />
                        <Route path="kullanim-kilavuzu" element={<AdminUsageGuide />} />
                        <Route path="blog" element={<AdminBlogPage />} />
                    </Route>
                    <Route path="/surveys/pending" element={<PendingSurveys />} />


                </Routes>

                {/* ğŸŒŸ Global Baby Name Modal - Shows when conditions are met */}
                {!isSharedGuestRoute && !loading && shouldRenderGlobalBabyNameModal && (
                    <>

                        <BabyNameModal
                            isOpen={shouldShowModal}
                            userProfile={userProfile}
                            onSave={saveBabyName}
                            onSkip={skipForNow}
                            onClose={hideModal}
                        />
                    </>
                )}
            </div>
    );

    if (isSharedGuestRoute) {
        return appRoutes;
    }

    return (
        <PriceProvider>
            <RealtimeBridge />
            {appRoutes}
        </PriceProvider>
    );
}

export default App;
